import { ObjectId } from "bson";
import * as z from "zod";

export const ObjectIdSchema = z.instanceof(ObjectId);

const MONGO_PREFIX = "m_";
const HEX_PATTERN = "[a-f0-9]{24}";

// Zod schema for BYTES columns from the DB (Prisma may return Uint8Array).
const BufferFromDbSchema = z.custom<Buffer>(
  (val): val is Buffer => Buffer.isBuffer(val) || val instanceof Uint8Array,
);

export interface PrefixedMongoIdSchema<T extends string> {
  readonly schema: z.ZodString;
  readonly fromDbBuffer: z.ZodType<string>;
  readonly make: (id: string) => string;
  readonly makeNew: () => string;
  readonly fromObjectId: (id: ObjectId) => string;
  readonly toObjectId: (id: string) => ObjectId;
  readonly toBuffer: (id: string) => Buffer;
  readonly fromBuffer: (buf: Buffer) => string;
  readonly prefix: string;
}

export function createPrefixedMongoIdSchema<T extends string>(
  typeName: T,
  prefix: string,
): PrefixedMongoIdSchema<T> {
  const fullPrefix = `${MONGO_PREFIX}${prefix}`;
  const regex = new RegExp(`^${fullPrefix}_${HEX_PATTERN}$`);

  const schema = z
    .string()
    .regex(regex, {
      message: `${typeName} must match ${fullPrefix}_<24 hex chars>`,
    });

  const fromObjectId = (id: ObjectId): string =>
    `${fullPrefix}_${id.toHexString()}`;

  const toObjectId = (id: string): ObjectId =>
    new ObjectId(id.slice(fullPrefix.length + 1));

  const toBuffer = (id: string): Buffer => Buffer.from(toObjectId(id).id);

  const fromBuffer = (buf: Buffer): string => fromObjectId(new ObjectId(buf));

  const make = (id: string): string => schema.parse(id);

  const makeNew = (): string => fromObjectId(new ObjectId());

  const fromDbBuffer = BufferFromDbSchema.transform(fromBuffer);

  return {
    schema,
    fromDbBuffer,
    make,
    makeNew,
    fromObjectId,
    toObjectId,
    toBuffer,
    fromBuffer,
    prefix: fullPrefix,
  };
}

/**
 * A bare ObjectId hex, branded so two ids of different kinds cannot be passed
 * for one another. Distinct from `createPrefixedMongoIdSchema`: this keeps the
 * Mongo-era wire format, for ids that are already exchanged as plain hex with
 * surfaces that read them straight out of Mongo.
 */
export function createObjectIdHexSchema<T extends string>(typeName: T) {
  return z
    .string()
    .regex(new RegExp(`^${HEX_PATTERN}$`, "i"), {
      message: `${typeName} must be a 24 character ObjectId hex`,
    })
    .brand<T>();
}

/** A Mongo user, as bare hex. `MongoUserId` is the prefixed form. */
export const MongoUserObjectId = createObjectIdHexSchema("User");
export type MongoUserObjectId = z.infer<typeof MongoUserObjectId>;

export const MongoFaqPageId = createObjectIdHexSchema("FaqPage");
export type MongoFaqPageId = z.infer<typeof MongoFaqPageId>;

export const MongoFaqCollectionId = createObjectIdHexSchema("FaqCollection");
export type MongoFaqCollectionId = z.infer<typeof MongoFaqCollectionId>;

export const MongoFaqArticleId = createObjectIdHexSchema("FaqArticle");
export type MongoFaqArticleId = z.infer<typeof MongoFaqArticleId>;

export const MongoIdPrefix = {
  Conversation: "cnv",
  Return: "ret",
  ReturnItem: "reti",
  Team: "tm",
  User: "us",
} as const;

/** A Support V1 conversation. V2 conversations are CRDB topics, not these. */
export const MongoConversationId = createPrefixedMongoIdSchema(
  "Conversation",
  MongoIdPrefix.Conversation,
);
export type MongoConversationId = ReturnType<typeof MongoConversationId.make>;

export const MongoReturnId = createPrefixedMongoIdSchema(
  "Return",
  MongoIdPrefix.Return,
);
export type MongoReturnId = ReturnType<typeof MongoReturnId.make>;

export const MongoReturnItemId = createPrefixedMongoIdSchema(
  "ReturnItem",
  MongoIdPrefix.ReturnItem,
);
export type MongoReturnItemId = ReturnType<typeof MongoReturnItemId.make>;

export const MongoTeamId = createPrefixedMongoIdSchema(
  "Team",
  MongoIdPrefix.Team,
);
export type MongoTeamId = ReturnType<typeof MongoTeamId.make>;

export const MongoUserId = createPrefixedMongoIdSchema(
  "User",
  MongoIdPrefix.User,
);
export type MongoUserId = ReturnType<typeof MongoUserId.make>;

export const ObjectIdCoerceSchema = z
  .custom<ObjectId>((data: any) => ObjectId.isValid(data))
  .transform((data) => new ObjectId(data))
  .meta({ annotation: "ObjectId" });
